'use client';

import { Button } from '@/components/button';
import { reveal } from '@/components/codeRevealer/actions/revealAction';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import { renderVoucherAction } from '@/components/pdf/actions/renderPdfAction';
import { openPdf } from '@/components/pdf/utils';
import { RichText } from '@/components/richText/RichText';
import type { VoucherPropsWithoutCode } from '@/helper/microservices/vorteilsweltService';
import { type ReplacementsType, replacePlaceholders } from '@/helper/replacements';
import tracking from '@/helper/tracking';
import { linkToString } from '@/helper/urls';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { ComponentVtwVoucherGenericStoryblok, PageVtwBenefitStoryblok } from '@/types/component-types-sb';
import { ErrorType, type Vorteil } from '@/types/vorteilswelt';
import type { ISbStoryData } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './CodeRevealer.module.scss';

interface CodeRevealerProps {
  story: ISbStoryData<PageVtwBenefitStoryblok>;
  voucherProps?: VoucherPropsWithoutCode;
  vorteil?: Vorteil;
  genericVoucherProps?: ComponentVtwVoucherGenericStoryblok;
  replacements: ReplacementsType;
  setError: (error: ErrorType) => void;
  alreadyRedeemed?: boolean;
}

const CodeRevealer: React.FC<CodeRevealerProps> = ({
  story,
  voucherProps,
  replacements,
  setError,
  vorteil,
  genericVoucherProps,
  alreadyRedeemed,
}) => {
  const { t } = useTranslation();
  const setErrorModalOpen = useHeaderStore((state) => state.setErrorModalOpen);
  const userStore = useUserStore((state) => ({
    getUserState: state.getUserState,
    trackingData: state.trackingData,
  }));

  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState(vorteil?.userCode ?? '');
  const [firstAnimationExited, setFirstAnimationExited] = useState(false);
  const [trackingPurchase, setTrackingPurchase] = useState<boolean>(false);
  const [codeRevealClick, setCodeRevealClick] = useState<boolean>(false);

  const animationTimeout = 0.4 * 1000;
  const easing = 'cubic-bezier(0.0, 0, 0.2, 1)';

  const blok = story.content;
  const name = replacePlaceholders(replacements, '%%name%%');

  useEffect(() => {
    if (vorteil) {
      setCode(vorteil.userCode);
      // Set codeRevealClick to true when vorteil is provided to trigger tracking
      if (vorteil.userCode) {
        // Call reveal function when vorteil has a user code, but only once
        if (!codeRevealClick && !alreadyRedeemed) {
          const callReveal = async () => {
            if (voucherProps) {
              setCodeRevealClick(true);
              await reveal({
                voucherProps: { ...voucherProps, code: vorteil.userCode },
                slug: story.full_slug,
              });
            }
          };

          callReveal();
        }
      }
    }
  }, [vorteil, voucherProps, userStore, story.full_slug, codeRevealClick, alreadyRedeemed]);

  useEffect(() => {
    // track purchase for codes
    if (codeRevealClick && code && !trackingPurchase && userStore?.trackingData && !alreadyRedeemed) {
      const trackingItem = userStore.trackingData;
      tracking.nonCommodityPurchase([trackingItem], {
        transactionId: `${trackingItem.itemId}_${Date.now()}`,
      });

      setTrackingPurchase(true);
    }
  }, [userStore, trackingPurchase, code, codeRevealClick, alreadyRedeemed]);

  const onRevealClick = async () => {
    if (loading) return; // Prevent multiple clicks
    setLoading(true); // Disable further clicks
    if (!voucherProps) {
      setError(ErrorType.DEFAULT);
      setLoading(false); // Re-enable click if there's an error
      return;
    }
    const res = await reveal({
      voucherProps: { ...voucherProps, code },
      slug: story.full_slug,
    });
    if ('error' in res) {
      setError(res.error);
      setLoading(false); // Re-enable click on error
      return;
    }
    setCode(res.code);
    setLoading(false); // Re-enable click after success
    setCodeRevealClick(true);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 4 * 1000);
  };

  const render = async () => {
    if (!voucherProps) {
      throw new Error('voucher props undefined');
    }
    const base64string = await renderVoucherAction({ ...voucherProps, code });
    return base64string;
  };

  const benefit = blok.benefit[0];
  const codeUnrevealed = (
    <div className={classes.BenefitContainer}>
      <Button variant="ctaFull" onClick={onRevealClick} datatest-id="benefit-container">
        {benefit?.revealbox_cta}
      </Button>
      <RichText text={benefit?.revealbox_text} replace={replacements} />
    </div>
  );

  const isAffiliateLink = code.startsWith('http');
  const couponText = isAffiliateLink ? t('Angebot nutzen') : t('vtw.codeHeading') + ':';
  const couponContent = isAffiliateLink ? null : code ? (
    <HeadlineField
      tag={HeadlineTag.H3}
      style={HeadlineTag.H5}
      className={classNames(classes.Headline)}
      data-testid="code"
    >
      {code}
    </HeadlineField>
  ) : (
    <span className={classes.codeSkeleton} aria-hidden />
  );

  const [hovered, setHovered] = useState(false);

  const couponButton = !isAffiliateLink && (
    <Button
      variant={copied ? 'ctaFull' : 'ctaWhite'}
      onClick={copyCode}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={!code}
      data-testid="copy-code"
    >
      {copied ? (hovered ? t('vtw.copyCodeAgain') : t('vtw.copiedCode')) : t('vtw.copyCode')}
    </Button>
  );

  const offerButton = (isAffiliateLink || blok.partner_link?.url) && (
    <Button
      data-testid="vtw-offer-button"
      variant="ctaFull"
      target="_blank"
      href={isAffiliateLink ? code : linkToString(blok.partner_link)}
    >
      {t('vtw.openOffer')}
    </Button>
  );

  const pdfButton = !isAffiliateLink && (
    <Button
      variant="ctaWhite"
      onClick={() => {
        try {
          openPdf(render);
        } catch (error) {
          setErrorModalOpen(true);
        }
      }}
      data-testid="vtw-open-pdf"
    >
      {t('vtw.openPdf')}
    </Button>
  );

  const appendingText = isAffiliateLink && (
    <div className={classes.AppendingText}>{genericVoucherProps?.infoText_cta ?? ''}</div>
  );

  const codeRevealed = (
    <div className={classes.CouponContainer}>
      <div className={classNames(classes.InnerContainer, classes.Left)}>
        <div className={classes.Vertical}>
          <span className={classes.LeftLabel}>{t('vtw.intro')}</span>
          <HeadlineField tag={HeadlineTag.H3} style={HeadlineTag.H6} className={classes.Headline}>
            {name}
          </HeadlineField>
        </div>
        <div className={classes.Vertical}>
          <span className={classes.LeftLabel}>{t('vtw.benefitHeading')}</span>
          <HeadlineField tag={HeadlineTag.H4} style={HeadlineTag.H6} className={classNames(classes.Headline)}>
            {blok.headline}
          </HeadlineField>
        </div>
      </div>

      <div className={classNames(classes.InnerContainer, classes.Right)}>
        <div className={classNames(classes.CouponText, classes.Vertical)}>
          <span className={classes.CouponLabel}>{couponText}</span>
          {couponContent}
        </div>
        <div className={classNames(classes.Actions)}>
          {couponButton}
          {pdfButton}
          {offerButton}
        </div>
        {appendingText}
      </div>
    </div>
  );

  if (!!vorteil) {
    return codeRevealed;
  }

  const showUnrevealed = !code || code === '';

  return (
    <div className={classes.RevealContainer}>
      <div
        className={classes.ZoomContainer}
        style={{
          opacity: showUnrevealed ? 1 : 0,
          transition: `opacity ${animationTimeout}ms ${easing}`,
          pointerEvents: showUnrevealed ? 'auto' : 'none',
        }}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'opacity' && !showUnrevealed) {
            setFirstAnimationExited(true);
          }
        }}
      >
        {codeUnrevealed}
      </div>
      <div
        className={classes.ZoomContainer}
        style={{
          opacity: firstAnimationExited && code !== '' ? 1 : 0,
          transition: `opacity ${animationTimeout}ms ${easing}`,
          pointerEvents: firstAnimationExited && code !== '' ? 'auto' : 'none',
        }}
      >
        {codeRevealed}
      </div>
    </div>
  );
};

export default CodeRevealer;
